import { useUsage } from '../queries';
import { Icon, usd } from '../ui';

const gb = (b: number) => (b / 1e9).toFixed(b >= 1e10 ? 1 : 2);
const min = (s: number) => (s / 60).toFixed(s >= 6000 ? 0 : 1);
const watched = (m: number | undefined) => (!m ? '—' : m < 60 ? `${m} min` : `${(m / 60).toFixed(m >= 6000 ? 0 : 1)} h`);

export function UsagePage() {
  const q = useUsage(30);
  if (q.isPending) return <section className="page muted">Loading…</section>;
  if (q.error) return <section className="page error-text">{q.error.message}</section>;
  const u = q.data;
  const available = Number(u.available_usd);
  const mode = u.billing_mode ?? 'prepaid';
  const internal = mode === 'internal';
  const canGoNegative = mode !== 'prepaid';
  const charged30 = u.days.reduce((a, d) => a + Number(d.cost_usd), 0);
  const ours30 = u.days.reduce((a, d) => a + Number(d.our_cost_usd ?? 0), 0);

  return (
    <section className="page">
      <div className="page-head">
        <div>
          <h1>Usage &amp; credit</h1>
          <p className="sub">
            {mode === 'prepaid'
              ? 'Prepaid credit, used as you stream.'
              : mode === 'postpaid'
                ? 'Postpaid: charged as you stream, and the balance can go below zero. Nothing stops for money; a negative balance is what you owe.'
                : 'Internal account: charged like a customer so the numbers are real, never stopped. A negative balance is what it has used.'}{' '}
            Usage updates hourly; each day is charged a couple of hours after it ends (UTC).
          </p>
        </div>
        {!internal && (
          <a className="btn" href="mailto:ops@justonair.com?subject=Credit">
            {mode === 'postpaid' && available < 0 ? 'Pay balance' : 'Request credit'}
          </a>
        )}
      </div>

      {u.balance_depleted_at && (
        <div className="notice" role="alert">
          <Icon name="info" size={14} />
          <span>
            Your credit ran out. Live streams end at zero, and recordings are deleted
            {u.recordings_delete_at ? ` on ${new Date(u.recordings_delete_at).toLocaleDateString()}` : ' 7 days later'} unless credit is added.
          </span>
        </div>
      )}

      <div className="grid-2">
        <section className="card">
          <h2>{canGoNegative && available < 0 ? (internal ? 'Used (balance)' : 'Owed') : 'Available now'}</h2>
          <span
            className="big"
            style={!internal && available < (canGoNegative ? 0 : 5) ? { color: 'var(--warn)' } : undefined}
          >
            {canGoNegative && available < 0 ? usd(-available) : usd(u.available_usd)}
          </span>
          <span className="faint" style={{ fontSize: 12 }}>
            Charged balance {usd(u.balance_usd)}, minus what has been used but not charged yet.{' '}
            {canGoNegative ? 'Streams never stop for money on this account.' : 'Streams stop when this reaches zero.'}
          </span>
        </section>
        {internal && (
          <section className="card">
            <h2>Last 30 days</h2>
            <div className="kv">
              <div>
                <span className="k">Charged (list prices)</span>
                <span className="mono">{usd(charged30)}</span>
              </div>
              <div>
                <span className="k">Our cost</span>
                <span className="mono">{usd(ours30)}</span>
              </div>
              <div>
                <span className="k">Fixed servers</span>
                <span className="mono">€{u.our_cost_rates?.fixed_eur_per_month ?? '—'} / month</span>
              </div>
            </div>
            <span className="faint" style={{ fontSize: 12 }}>
              Our cost: delivery ${u.our_cost_rates?.delivery_usd_per_gb}/GB, storage ${u.our_cost_rates?.storage_usd_per_gb_month}/GB-month.
              Encoding runs on the fixed servers, so it is $0 per stream.
            </span>
          </section>
        )}
        <section className="card">
          <h2>Prices</h2>
          <div className="kv">
            <div>
              <span className="k">Delivery</span>
              <span className="mono">${u.rates.delivery_usd_per_gb} / GB</span>
            </div>
            <div>
              <span className="k">Adaptive encoding</span>
              <span className="mono">${u.rates.transcode_usd_per_minute} / min</span>
            </div>
            <div>
              <span className="k">Passthrough</span>
              <span className="mono">${u.rates.passthrough_usd_per_minute} / min</span>
            </div>
            <div>
              <span className="k">Recordings</span>
              <span className="mono">${u.rates.storage_usd_per_gb_month} / GB-month</span>
            </div>
          </div>
        </section>
      </div>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2>Last 30 days</h2>
        {u.days.length === 0 ? (
          <div className="table">
            <div className="empty">No usage yet.</div>
          </div>
        ) : (
          <div className={`table usage${internal ? ' with-cost' : ''}`} role="table" aria-label="Daily usage">
            <div className="tr th" role="row">
              <span role="columnheader">Day (UTC)</span>
              <span role="columnheader">Delivered</span>
              <span role="columnheader">Watched</span>
              <span role="columnheader">Encoding</span>
              <span role="columnheader">Passthrough</span>
              <span role="columnheader">Recordings</span>
              <span role="columnheader">Cost</span>
              {internal && <span role="columnheader">Our cost</span>}
              <span role="columnheader">Charged</span>
            </div>
            {u.days.map((d) => (
              <div key={d.date} className="tr" role="row">
                <span role="cell" className="mono">{d.date}</span>
                <span role="cell" className="cell-mono">{gb(d.delivery_bytes)} GB</span>
                <span role="cell" className="cell-mono">{watched(d.watch_minutes)}</span>
                <span role="cell" className="cell-mono">{min(d.transcode_seconds)} min</span>
                <span role="cell" className="cell-mono">{min(d.passthrough_seconds)} min</span>
                <span role="cell" className="cell-mono">{d.storage_gb_hours} GB-h</span>
                <span role="cell" className="mono">{usd(d.cost_usd, 3)}</span>
                {internal && <span role="cell" className="mono">{usd(d.our_cost_usd ?? 0, 3)}</span>}
                <span role="cell" className={d.charged ? 'muted' : 'faint'}>{d.charged ? 'Charged' : 'Estimate'}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </section>
  );
}
